import { Component, Input, Output, EventEmitter, OnInit, signal } from '@angular/core';

import { FormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatAutocompleteModule, MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { MatChipsModule } from '@angular/material/chips';
import { ContractFilter, FilterOption } from '../../../interfaces/contract-filter.interface';
import { UserPreferencesService } from '../../../services/user-preferences.service';

@Component({
  selector: 'app-shared-filters',
  imports: [
    FormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatButtonModule,
    MatIconModule,
    MatExpansionModule,
    MatTooltipModule,
    MatAutocompleteModule,
    MatChipsModule
],
  templateUrl: './shared-filters.component.html',
  styleUrl: './shared-filters.component.scss',
  host: {
    '[class.sticky-filter]': 'isSticky()',
    '[style.--sticky-offset]': 'stickyOffsetVar'
  }
})
export class SharedFiltersComponent implements OnInit {
  // Sticky filter beállítások
  @Input() pageName: string = ''; // Oldal azonosító a sticky beállítás mentéséhez
  @Input() allowSticky: boolean = true; // Engedélyezi/tiltja a sticky opciót
  @Input() stickyOffset: number = 24; // Szülő konténer padding-ja (px), amit sticky módban kompenzálni kell
  isSticky = signal(false);

  get stickyOffsetVar() {
    return this.stickyOffset + 'px';
  }

  @Input() initialFilter: ContractFilter = {
    searchTerm: '',
    statusFilter: [],
    biztositoFilter: [],
    assigneeFilter: [],
    dateFromFilter: null,
    dateToFilter: null,
    productFilter: [],
    paymentFrequencyFilter: [],
    currencyFilter: [],
    policyNumberFilter: '',
    illustrationStatusFilter: []
  };

  @Input() showOnlySearch: boolean = false; // New input to show only search field
  @Input() customStatusOptions: { value: string; label: string }[] = []; // Custom status options for different pages
  @Input() customBiztositoOptions: { value: string; label: string }[] = []; // Custom biztosító options for contracts page
  @Input() customUserOptions: { value: string; label: string }[] = []; // Custom user options for logs page
  @Input() callCenterStaff: any[] = []; // Call center staff for assignee filtering
  @Input() useAssigneeFilter: boolean = false; // Switch between biztosito and assignee filter
  @Input() hideProductFields: boolean = false; // Hide product, payment frequency, and currency fields
  @Input() useLogsDateLabels: boolean = false; // Use logs-specific date labels instead of contract labels
  @Input() useLogsLabels: boolean = false; // Use logs-specific labels (Típus instead of Státusz, Felhasználó instead of Biztosító)
  @Input() lockAssigneeFilter: boolean = false; // Lock assignee filter for Call Center employees
  @Input() lockUserFilter: boolean = false; // Lock user filter for restricted roles
  @Input() lockStatusFilter: boolean = false; // Lock status filter (e.g., KÜM filter for KÜM users)
  @Input() customSearchPlaceholder: string = ''; // Custom placeholder text for search field
  @Input() customStatusLabel: string = ''; // Custom label for status filter (e.g., 'KÜM')
  @Input() customBiztositoLabel: string = ''; // Custom label for biztosito filter (e.g., 'Terület')
  @Input() customDateFromLabel: string = ''; // Custom label for date from filter
  @Input() customDateToLabel: string = ''; // Custom label for date to filter
  @Input() hidePolicyNumberField: boolean = false; // Hide policy number field
  @Input() showIllustrationStatusFilter: boolean = false; // Show illustration status filter in advanced section
  @Input() illustrationStatusOptions: { value: string; label: string }[] = []; // Illustration status options
  @Input() showMainActivityFilter: boolean = false; // Show main activity autocomplete in advanced section
  @Input() mainActivityOptions: string[] = []; // Main activity suggestions provided by the parent component

  @Output() filterChange = new EventEmitter<ContractFilter>();
  @Output() clearFilters = new EventEmitter<void>();
  @Output() mainActivitySearch = new EventEmitter<string>();

  currentFilter: ContractFilter = {
    searchTerm: '',
    statusFilter: [],
    biztositoFilter: [],
    assigneeFilter: [],
    dateFromFilter: null,
    dateToFilter: null,
    productFilter: [],
    paymentFrequencyFilter: [],
    currencyFilter: [],
    policyNumberFilter: '',
    illustrationStatusFilter: [],
    mainActivityFilter: []
  };

  // A Főtevékenység mezőbe gépelt szöveg (nem szűr, csak javaslatokat kér)
  mainActivityInput: string = '';

  // Expansion panel state
  isAdvancedFiltersExpanded = false;

  // Track previous assignee filter state to handle "all" option correctly
  private previousAssigneeFilter: string[] = [];

  // Default status options - will be overridden by customStatusOptions if provided
  defaultStatusOptions: FilterOption[] = [
    { value: 'élő', label: 'élő' },
    { value: 'élő, díjmentesített', label: 'élő, díjmentesített' },
    { value: 'élő, szünetelteve', label: 'élő, szünetelteve' },
    { value: 'visszavásárolt', label: 'visszavásárolt' },
    { value: 'törölt', label: 'törölt' },
    { value: 'elutasított', label: 'elutasított' },
    { value: 'ajánlat', label: 'ajánlat' },
    { value: 'Törölt', label: 'Törölt' }
  ];

  // Dynamic status options - uses customStatusOptions if provided, otherwise defaultStatusOptions
  get statusOptions(): FilterOption[] {
    return this.customStatusOptions.length > 0 ? this.customStatusOptions : this.defaultStatusOptions;
  }

  // Default biztosító options - will be overridden by customBiztositoOptions if provided
  defaultBiztositoOptions: FilterOption[] = [
    { value: 'Alfa', label: 'Alfa' },
    { value: 'Metlife', label: 'Metlife' },
    { value: 'Allianz', label: 'Allianz' },
    { value: 'Generali', label: 'Generali' },
    { value: 'Uniqa', label: 'Uniqa' },
    { value: 'Union', label: 'Union' }
  ];

  // Dynamic biztosító options - uses customBiztositoOptions if provided, otherwise defaultBiztositoOptions
  get biztositoOptions(): FilterOption[] {
    return this.customBiztositoOptions.length > 0 ? this.customBiztositoOptions : this.defaultBiztositoOptions;
  }

  // Dynamic user options - uses customUserOptions for logs pages
  get userOptions(): FilterOption[] {
    return this.customUserOptions;
  }

  // Advanced filter options
  productOptions: FilterOption[] = [
    { value: 'MetProtect', label: 'MetProtect' },
    { value: 'Manhattan (€)', label: 'Manhattan (€)' },
    { value: 'Manhattan (Ft)', label: 'Manhattan (Ft)' }
  ];

  paymentFrequencyOptions: FilterOption[] = [
    { value: 'havi', label: 'havi' },
    { value: 'negyedéves', label: 'negyedéves' },
    { value: 'féléves', label: 'féléves' },
    { value: 'éves', label: 'éves' },
    { value: 'egyszeri', label: 'egyszeri' }
  ];

  currencyOptions: FilterOption[] = [
    { value: 'HUF', label: 'HUF (Forint)' },
    { value: 'EUR', label: 'EUR (Euró)' }
  ];

  constructor(private userPreferencesService: UserPreferencesService) {}

  ngOnInit() {
    this.currentFilter = { ...this.initialFilter };

    if (!Array.isArray(this.currentFilter.mainActivityFilter)) {
      this.currentFilter.mainActivityFilter = [];
    }
    
    // Sticky filter beállítás betöltése a felhasználói preferenciákból
    if (this.pageName && this.allowSticky) {
      this.userPreferencesService.getStickyFilterSetting(this.pageName).subscribe({
        next: (result) => {
          this.isSticky.set(result.sticky);
        },
        error: () => {
          // Ha nem sikerül betölteni, alapértelmezett: nem sticky
          this.isSticky.set(false);
        }
      });
    }

    // Initialize assignee filter with 'all' for call center mode
    if (this.useAssigneeFilter && (!this.currentFilter.assigneeFilter || this.currentFilter.assigneeFilter.length === 0)) {
      this.currentFilter.assigneeFilter = ['all'];
    }
    
    // Initialize previous assignee filter state - only if assigneeFilter exists and is an array
    if (this.useAssigneeFilter && Array.isArray(this.currentFilter.assigneeFilter)) {
      this.previousAssigneeFilter = [...(this.currentFilter.assigneeFilter as string[])];
    } else {
      this.previousAssigneeFilter = [];
    }
    
    // Emit initial filter state
    this.emitFilterChange();
  }

  /**
   * Sticky szűrő állapot váltása és mentése a felhasználói preferenciákba
   */
  toggleSticky() {
    this.isSticky.set(!this.isSticky());
    if (this.pageName) {
      this.userPreferencesService.saveStickyFilterSetting(this.pageName, this.isSticky()).subscribe({
        error: (err) => {
          console.error('Sticky filter beállítás mentése sikertelen:', err);
        }
      });
    }
  }


  // Call center specific filter methods
  private applyCallCenterFilters() {
    if (!this.useAssigneeFilter) {
      this.emitFilterChange();
      return;
    }

    // Emit the filtered data with call center specific format
    this.emitFilterChange();
  }

  onSearchChange() {
    this.emitFilterChange();
  }

  onStatusFilterChange() {
    this.emitFilterChange();
  }

  onBiztositoFilterChange() {
    this.emitFilterChange();
  }

  onAssigneeSelectionChange(event: any) {
    // Call center specific assignee filter logic
    if (!this.useAssigneeFilter) {
      this.emitFilterChange();
      return;
    }

    const currentSelection = event.value as string[];
    const previousSelection = this.previousAssigneeFilter;
    
    // If nothing is selected, automatically select "all"
    if (!currentSelection || currentSelection.length === 0) {
      this.currentFilter.assigneeFilter = ['all'];
      this.previousAssigneeFilter = ['all'];
      this.emitFilterChange();
      return;
    }

    // Handle "all" option logic
    if (currentSelection.includes('all')) {
      // If "all" wasn't in the previous selection, user just clicked "all"
      if (!previousSelection.includes('all')) {
        // User clicked "all", clear everything else and select only "all"
        this.currentFilter.assigneeFilter = ['all'];
      } else if (currentSelection.length > 1) {
        // "all" was already selected and user added a specific option
        // Remove "all" and keep the specific selections
        this.currentFilter.assigneeFilter = currentSelection.filter(value => value !== 'all');
      }
      // If only "all" is selected and was selected before, keep it as is
    } else {
      // No "all" in current selection, keep as is
      this.currentFilter.assigneeFilter = currentSelection;
    }

    // Update previous state - only if assigneeFilter exists and is an array
    if (Array.isArray(this.currentFilter.assigneeFilter)) {
      this.previousAssigneeFilter = [...(this.currentFilter.assigneeFilter as string[])];
    } else {
      this.previousAssigneeFilter = [];
    }
    
    this.applyCallCenterFilters();
  }

  onDateFromChange() {
    this.emitFilterChange();
  }

  onDateToChange() {
    this.emitFilterChange();
  }

  // Advanced filter methods
  onProductFilterChange() {
    this.emitFilterChange();
  }

  onPaymentFrequencyFilterChange() {
    this.emitFilterChange();
  }

  onCurrencyFilterChange() {
    this.emitFilterChange();
  }

  onPolicyNumberFilterChange() {
    this.emitFilterChange();
  }

  onIllustrationStatusFilterChange() {
    this.emitFilterChange();
  }

  /**
   * Gépeléskor javaslatokat kér a szülő komponenstől (a szűrés csak kiválasztáskor történik)
   */
  onMainActivityInput(value: string) {
    this.mainActivityInput = value ?? '';
    this.mainActivitySearch.emit(this.mainActivityInput);
  }

  onMainActivityFocus() {
    this.mainActivitySearch.emit(this.mainActivityInput);
  }

  onMainActivitySelected(event: MatAutocompleteSelectedEvent, inputElement: HTMLInputElement) {
    const selected = event.option.value as string;
    const current = (this.currentFilter.mainActivityFilter ?? []) as string[];

    if (selected && !current.includes(selected)) {
      this.currentFilter.mainActivityFilter = [...current, selected];
      this.emitFilterChange();
    }

    this.mainActivityInput = '';
    inputElement.value = '';
  }

  removeMainActivity(activity: string) {
    const current = (this.currentFilter.mainActivityFilter ?? []) as string[];
    this.currentFilter.mainActivityFilter = current.filter(a => a !== activity);
    this.emitFilterChange();
  }

  onClearFilters() {
    this.currentFilter = {
      searchTerm: '',
      statusFilter: [],
      biztositoFilter: [],
      assigneeFilter: this.useAssigneeFilter ? ['all'] : [], // Default to 'all' for call center
      dateFromFilter: null,
      dateToFilter: null,
      productFilter: [],
      paymentFrequencyFilter: [],
      currencyFilter: [],
      policyNumberFilter: '',
      illustrationStatusFilter: [],
      mainActivityFilter: []
    };
    this.mainActivityInput = '';
    this.isAdvancedFiltersExpanded = false;
    
    // Emit the cleared filters
    this.emitFilterChange();
    this.clearFilters.emit();
  }

  /**
   * Visszaadja a Hozzárendelve mező összefoglaló szövegét a zárt állapothoz
   */
  getAssigneeDisplayText(): string {
    const selected = this.currentFilter.assigneeFilter as string[];
    if (!selected || selected.length === 0) return '';
    if (selected.length >= 2) return `${selected.length} kiválasztva`;
    if (selected[0] === 'all') return 'Összes';
    if (selected[0] === 'unassigned') return 'Nincs hozzárendelve';
    return selected[0];
  }

  /**
   * Igaz, ha 2 vagy több opció van kiválasztva a Hozzárendelve mezőben
   */
  isAssigneeMultiSelected(): boolean {
    const selected = this.currentFilter.assigneeFilter as string[];
    return Array.isArray(selected) && selected.length >= 2;
  }

  private emitFilterChange() {
    // Create filter data based on context (call center vs contracts)
    let filterToEmit = { ...this.currentFilter };

    if (this.useAssigneeFilter) {
      // Call center specific processing
      const assigneeFilter = filterToEmit.assigneeFilter as string[];
      
      // If "all" is selected, send empty array (no filtering)
      if (assigneeFilter && assigneeFilter.includes('all')) {
        filterToEmit.assigneeFilter = [];
      }
    }

    this.filterChange.emit(filterToEmit);
  }
}